// ParametersTab.js — NEW component
// Shows the parameters submitted when the domain was enrolled (from data.submitted).
// This mirrors what was in the collapsed "Submitted Details" panel in the old DomainInfoTab,
// now promoted to its own dedicated tab for discoverability.

import React, { useState } from "react";
import { Card, Descriptions, Select, Spin } from "antd";
import axios from "axios";
import Cookies from "js-cookie";
import Swal from "sweetalert2";
import { API_DOMAIN_MONITOR } from "../../../config/config";

const { Option } = Select;

const FREQUENCY_OPTIONS = [
    { value: "7", labelKey: "domain_monitor.freq_7_days" },
    { value: "30", labelKey: "domain_monitor.freq_30_days" },
    { value: "90", labelKey: "domain_monitor.freq_90_days" }
];

const ParametersTab = ({ data, onRefresh, t = (key) => key }) => {
    const [freqLoading, setFreqLoading] = useState(false);

    if (!data) return null;

    // Destructure submitted fields — all are optional depending on what was provided at enrollment
    const submitted = data?.submitted || {};
    const contact = submitted.contact || {};
    const address = submitted.address || {};

    const currentFrequency = data?.checkFrequency != null ? String(data.checkFrequency) : undefined;

    const handleFrequencyChange = async (value) => {
        const confirm = await Swal.fire({
            title: t("domain_monitor.confirm_action"),
            text: t("domain_monitor.confirm_change_frequency"),
            icon: "question",
            showCancelButton: true,
            confirmButtonText: t("domain_monitor.yes_proceed"),
            cancelButtonText: t("domain_monitor.cancel")
        });
        if (!confirm.isConfirmed) return;

        setFreqLoading(true);
        try {
            await axios.patch(
                `${API_DOMAIN_MONITOR}/v1/domains/${data.id}`,
                { checkFrequency: value },
                {
                    headers: {
                        Authorization: "Bearer " + Cookies.get("dm_token")
                    }
                }
            );
            Swal.fire({
                icon: "success",
                title: t("domain_monitor.success"),
                text: t("domain_monitor.frequency_updated")
            });
            if (onRefresh) onRefresh();
        } catch (error) {
            Swal.fire({
                icon: "error",
                title: t("domain_monitor.error"),
                text:
                    error.response?.data?.error?.message ||
                    error.message ||
                    t("domain_monitor.unexpected_error")
            });
        } finally {
            setFreqLoading(false);
        }
    };

    return (
        <Card>
            <Descriptions title={t("domain_monitor.tab_domain_parameters")} bordered column={2}>
                {/* Core domain identifier */}
                <Descriptions.Item label={t("domain_monitor.domain_name")} span={2}>
                    <strong>{data?.domain || "-"}</strong>
                </Descriptions.Item>

                {/* Monitoring frequency — editable */}
                <Descriptions.Item label={t("domain_monitor.check_frequency")} span={2}>
                    <Spin spinning={freqLoading} size="small">
                        <Select
                            style={{ minWidth: 200 }}
                            value={currentFrequency}
                            onChange={handleFrequencyChange}
                            disabled={freqLoading}
                            placeholder={t("domain_monitor.check_frequency")}
                        >
                            {FREQUENCY_OPTIONS.map((opt) => (
                                <Option key={opt.value} value={opt.value}>
                                    {t(opt.labelKey)}
                                </Option>
                            ))}
                        </Select>
                    </Spin>
                </Descriptions.Item>

                {/* Business info — falls back to data.name if submitted.businessName is absent */}
                <Descriptions.Item label={t("domain_monitor.business_name")}>
                    {submitted.businessName || data?.name || "-"}
                </Descriptions.Item>
                <Descriptions.Item label={t("domain_monitor.website")}>
                    {submitted.website
                        ? <a href={submitted.website} target="_blank" rel="noopener noreferrer">{submitted.website}</a>
                        : "-"
                    }
                </Descriptions.Item>
                <Descriptions.Item label={t("domain_monitor.description_label")} span={2}>
                    {submitted.description || "-"}
                </Descriptions.Item>

                {/* Contact details */}
                <Descriptions.Item label={t("domain_monitor.full_name")}>
                    {contact.fullName || "-"}
                </Descriptions.Item>
                <Descriptions.Item label={t("domain_monitor.email")}>
                    {contact.email || "-"}
                </Descriptions.Item>
                <Descriptions.Item label={t("domain_monitor.phone")}>
                    {contact.phone || "-"}
                </Descriptions.Item>

                {/* Address */}
                <Descriptions.Item label={t("domain_monitor.address_line_1")}>
                    {address.line1 || "-"}
                </Descriptions.Item>
                <Descriptions.Item label={t("domain_monitor.address_line_2")}>
                    {address.line2 || "-"}
                </Descriptions.Item>
                <Descriptions.Item label={t("domain_monitor.city")}>
                    {address.city || "-"}
                </Descriptions.Item>
                <Descriptions.Item label={t("domain_monitor.state_province")}>
                    {address.stateProvince || "-"}
                </Descriptions.Item>
                <Descriptions.Item label={t("domain_monitor.postal_code")}>
                    {address.postalCode || "-"}
                </Descriptions.Item>
                <Descriptions.Item label={t("domain_monitor.country")}>
                    {address.country || "-"}
                </Descriptions.Item>
            </Descriptions>
        </Card>
    );
};

export default ParametersTab;
